import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fade,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  ThemeProvider,
  createTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import PersonIcon from "@mui/icons-material/Person";
import ScheduleIcon from "@mui/icons-material/Schedule";
import EventIcon from "@mui/icons-material/Event";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import RefreshIcon from "@mui/icons-material/Refresh";
import PhoneCallbackIcon from "@mui/icons-material/PhoneCallback";
import AssignmentIcon from "@mui/icons-material/Assignment";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import PaymentIcon from "@mui/icons-material/Payment";
import FilterListIcon from "@mui/icons-material/FilterList";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import InboxIcon from "@mui/icons-material/Inbox";
import { toast } from "react-toastify";

import { fetchWithAuth } from "../Components/FetchWithAuth";
import { url } from "../Components/url";
import { schedulingApi } from "../Components/schedulingApi";
import ApplicationFormModal from "../Components/ApplicationForm/ApplicationFormModal";
import { UserContext } from "../Contexts/LoginContext";
import { RoleContext } from "../Contexts/RoleContext";
import { EnquiryContext } from "../Contexts/EnquiryContext";
import { GlobalSearchBar } from "../Components/ERPList";
import { useDebouncedValue } from "../hooks/useDebouncedValue";
import { useTheme } from "../hooks/useTheme";
import { createMuiThemeFromMode } from "../theme/theme";

import styles from "./AllEnquiries.module.scss";

const PAGE_SIZE_OPTIONS = [10, 12, 25, 50, 100];

const TYPE_LABEL = {
  enquiry: "Enquiry",
  follow_up: "Follow-up",
  appointment: "Upcoming appointment",
  pending_payment: "Pending payment",
  reschedule: "Reschedule request",
  session_approval: "Session approval",
};

const TYPE_ICON = {
  enquiry: <PersonIcon fontSize="small" />,
  follow_up: <ScheduleIcon fontSize="small" />,
  appointment: <EventAvailableIcon fontSize="small" />,
  pending_payment: <PaymentIcon fontSize="small" />,
  reschedule: <ScheduleIcon fontSize="small" />,
  session_approval: <EventIcon fontSize="small" />,
};

const FilterPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(3),
  backgroundColor: "#ffffff",
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
}));

function formatDue(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return d.toLocaleDateString(undefined, { dateStyle: "short" }) + " " + d.toLocaleTimeString(undefined, { timeStyle: "short" });
}

export default function TasksPage() {
  const { themeMode } = useTheme();
  const baseThemeOptions = useMemo(
    () => ({
      palette: {
        secondary: { main: "#f5365c" },
        success: { main: "#2dce89" },
        warning: { main: "#fb6340" },
        info: { main: "#11cdef" },
      },
      typography: {
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
        h4: { fontWeight: 600, letterSpacing: "-0.02em" },
        button: { textTransform: "none", fontWeight: 500 },
      },
      shape: { borderRadius: 12 },
      components: {
        MuiButton: { styleOverrides: { root: { boxShadow: "none" } } },
        MuiPaper: { styleOverrides: { root: { backgroundImage: "none" } } },
      },
    }),
    []
  );
  const theme = useMemo(
    () => createTheme(createMuiThemeFromMode(themeMode, baseThemeOptions)),
    [themeMode, baseThemeOptions]
  );
  const navigate = useNavigate();
  const { username } = useContext(UserContext);
  const { role } = useContext(RoleContext);
  const { setEnquiry } = useContext(EnquiryContext);
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusLoadingId, setStatusLoadingId] = useState(null);
  const [globalSearch, setGlobalSearch] = useState("");
  const debouncedGlobalSearch = useDebouncedValue(globalSearch, 400);
  const [filterOpen, setFilterOpen] = useState(!isMobile);
  const [filterType, setFilterType] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);

  const [contactedModalOpen, setContactedModalOpen] = useState(false);
  const [contactedEnquiry, setContactedEnquiry] = useState(null);
  const [contactLeadStatus, setContactLeadStatus] = useState("");
  const [contactCommentText, setContactCommentText] = useState("");
  const [contactSubmitLoading, setContactSubmitLoading] = useState(false);
  const [contactError, setContactError] = useState(null);
  const [applicationFormModalOpen, setApplicationFormModalOpen] = useState(false);
  const [applicationFormEnquiry, setApplicationFormEnquiry] = useState(null);

  const isAdminOrManager = role === "admin" || role === "Manager";

  const appointmentsListFallbackPath = useMemo(() => {
    if (role === "Counsellor" || role === "Visa Counsellor" || role === "Visa&Application Counsellor") {
      return "/appointments/my";
    }
    if (role === "admin" || role === "Manager") return "/appointments/all";
    return "/appointments/calendar";
  }, [role]);

  const loadTasks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetchWithAuth(`${url}/tasks`, { method: "GET", credentials: "include" });
      const data = await r.json();
      if (!r.ok) {
        setError(data?.error || "Failed to load tasks");
        setTasks([]);
        return;
      }
      let list = Array.isArray(data?.tasks) ? data.tasks : [];

      if (isAdminOrManager) {
        try {
          const [rescheduleData, eventsData] = await Promise.all([
            schedulingApi.listRescheduleRequests({ status: "pending" }),
            schedulingApi.listPendingApprovalEvents(),
          ]);
          const rescheduleList = Array.isArray(rescheduleData?.requests) ? rescheduleData.requests : rescheduleData || [];
          rescheduleList.forEach((req) => {
            list.push({
              type: "reschedule",
              id: `reschedule-${req.id}`,
              title: req.event_title ? `Reschedule: ${req.event_title}` : `Reschedule request #${req.id}`,
              status: req.status || "pending",
              due_date: req.requested_start_datetime || null,
              overdue: false,
              link: "/schedule/reschedule-requests",
            });
          });
          const eventsList = Array.isArray(eventsData?.events) ? eventsData.events : eventsData || [];
          eventsList.forEach((ev) => {
            list.push({
              type: "session_approval",
              id: `session-${ev.id}`,
              title: ev.title ? `Approve session: ${ev.title}` : `Session #${ev.id}`,
              status: "pending_approval",
              due_date: null,
              overdue: false,
              link: "/schedule/approvals",
            });
          });
        } catch (_) {}
      }

      setTasks(list);
    } catch (e) {
      setError(e?.message || "Failed to load tasks");
      setTasks([]);
    } finally {
      setLoading(false);
    }
  }, [isAdminOrManager]);

  // Client-side filtering and pagination
  const filteredBySearch = useMemo(() => {
    const q = (debouncedGlobalSearch || "").trim().toLowerCase();
    if (!q) return tasks;
    return tasks.filter((t) => {
      const title = (t.title || "").toLowerCase();
      const status = (t.status || "").toLowerCase();
      const typeLabel = (TYPE_LABEL[t.type] || t.type || "").toLowerCase();
      return title.includes(q) || status.includes(q) || typeLabel.includes(q);
    });
  }, [tasks, debouncedGlobalSearch]);

  const filteredByFilters = useMemo(() => {
    let list = filteredBySearch;
    if (filterType) {
      list = list.filter((t) => t.type === filterType);
    }
    if (filterStatus) {
      list = list.filter((t) => (t.status || "").trim() === filterStatus);
    }
    return list;
  }, [filteredBySearch, filterType, filterStatus]);

  const totalCount = filteredByFilters.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const page = Math.min(currentPage, totalPages);
  const displayData = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredByFilters.slice(start, start + pageSize);
  }, [filteredByFilters, page, pageSize]);

  const statusOptions = useMemo(() => {
    const set = new Set();
    tasks.forEach((t) => {
      if ((t.status || "").trim()) set.add((t.status || "").trim());
    });
    return Array.from(set).sort();
  }, [tasks]);

  const change_status = useCallback(
    async (enquiryId, newStatus) => {
      setStatusLoadingId(enquiryId);
      try {
        const response = await fetchWithAuth(`${url}/update_status`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            enquiry_id: enquiryId,
            status: newStatus,
            assigned_by: username,
          }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          toast.error(data?.error || "Failed to update status");
          return;
        }
        toast.success("Status updated");
        loadTasks();
      } catch (e) {
        toast.error(e?.message || "Failed to update status");
      } finally {
        setStatusLoadingId(null);
      }
    },
    [username, loadTasks]
  );

  const openContactedModal = useCallback((enquiry) => {
    setContactedEnquiry(enquiry);
    setContactLeadStatus("");
    setContactCommentText("");
    setContactError(null);
    setContactedModalOpen(true);
  }, []);

  const closeContactedModal = useCallback(() => {
    if (!contactSubmitLoading) {
      setContactedModalOpen(false);
      setContactedEnquiry(null);
      setContactLeadStatus("");
      setContactCommentText("");
      setContactError(null);
    }
  }, [contactSubmitLoading]);

  const handleContactedSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      const leadStatus = (contactLeadStatus || "").trim();
      const text = (contactCommentText || "").trim();
      if (!leadStatus || !text) {
        setContactError("Please select lead status (Hot/Warm/Cold) and enter a comment.");
        return;
      }
      setContactSubmitLoading(true);
      setContactError(null);
      try {
        const fullText = `${leadStatus} - ${text}`;
        const commentRes = await fetchWithAuth(`${url}/enquiry/communication`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            c_made_by: username,
            made_for: contactedEnquiry.id,
            text: fullText,
          }),
        });
        if (!commentRes.ok) {
          const errData = await commentRes.json().catch(() => ({}));
          setContactError(errData?.error || "Failed to add comment.");
          setContactSubmitLoading(false);
          return;
        }
        await change_status(contactedEnquiry.id, "Contacted");
        closeContactedModal();
      } catch (err) {
        setContactError("An error occurred. Please try again.");
      } finally {
        setContactSubmitLoading(false);
      }
    },
    [contactLeadStatus, contactCommentText, contactedEnquiry, username, change_status, closeContactedModal]
  );

  const handleOpenEnquiry = useCallback(
    (task) => {
      if (task.enquiry) {
        setEnquiry && setEnquiry(task.enquiry);
        navigate("/enquiry_profile", { state: { enquiry: task.enquiry } });
      }
    },
    [navigate, setEnquiry]
  );

  const handleAllocateEnquiry = useCallback(
    (task) => {
      if (task.enquiry) {
        setEnquiry && setEnquiry(task.enquiry);
        navigate("/enquiry_profile", { state: { enquiry: task.enquiry, openTab: "Courses" } });
      }
    },
    [navigate, setEnquiry]
  );

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  const handleOpenTask = (task) => {
    if (task.type === "enquiry" && task.enquiry) {
      handleOpenEnquiry(task);
    } else if (task.type === "pending_payment" && task.enquiry) {
      setEnquiry && setEnquiry(task.enquiry);
      navigate("/enquiry_profile", { state: { enquiry: task.enquiry } });
    } else if (task.link) {
      navigate(task.link);
    }
  };

  const resetFilters = () => {
    setGlobalSearch("");
    setFilterType("");
    setFilterStatus("");
    setCurrentPage(1);
    toast.info("Filters cleared.");
  };

  const renderActionButtons = (task) => {
    const loading = statusLoadingId !== null && task.type === "enquiry" && statusLoadingId === task.id;
    const stop = (e) => e.stopPropagation();

    if (task.type === "enquiry" && task.enquiry) {
      const status = (task.status || "").trim();
      const buttons = [];
      if (status === "New Enquiry") {
        buttons.push(
          <Button
            key="contacted"
            size="small"
            variant="contained"
            startIcon={<PhoneCallbackIcon />}
            disabled={loading}
            onClick={(e) => {
              stop(e);
              openContactedModal(task.enquiry);
            }}
          >
            {loading ? "Saving…" : "Contacted"}
          </Button>
        );
      }
      if (status === "Contacted") {
        buttons.push(
          <Button
            key="appform"
            size="small"
            variant="contained"
            startIcon={<AssignmentIcon />}
            disabled={loading}
            onClick={(e) => {
              stop(e);
              setApplicationFormEnquiry(task.enquiry);
              setApplicationFormModalOpen(true);
            }}
          >
            Application Form Filled
          </Button>,
          <Button
            key="future"
            size="small"
            variant="outlined"
            startIcon={<PersonAddIcon />}
            disabled={loading}
            onClick={(e) => {
              stop(e);
              change_status(task.id, "Future Lead");
            }}
          >
            {loading ? "Saving…" : "Future Lead"}
          </Button>
        );
      }
      if (status === "Future Lead") {
        buttons.push(
          <Button
            key="appform"
            size="small"
            variant="contained"
            startIcon={<AssignmentIcon />}
            disabled={loading}
            onClick={(e) => {
              stop(e);
              setApplicationFormEnquiry(task.enquiry);
              setApplicationFormModalOpen(true);
            }}
          >
            Move to Application Form Filled
          </Button>
        );
      }
      if (status === "Awaiting Session Allocation") {
        buttons.push(
          <Button
            key="allocate"
            size="small"
            variant="contained"
            startIcon={<CalendarMonthIcon />}
            disabled={loading}
            onClick={(e) => {
              stop(e);
              handleAllocateEnquiry(task);
            }}
          >
            Allocate
          </Button>
        );
      }
      buttons.push(
        <Button
          key="open"
          size="small"
          variant="outlined"
          startIcon={<OpenInNewIcon />}
          onClick={(e) => {
            stop(e);
            handleOpenEnquiry(task);
          }}
        >
          Open
        </Button>
      );
      return (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap onClick={stop}>
          {buttons}
        </Stack>
      );
    }

    if (task.type === "pending_payment") {
      const isEnquiryProfile = task.link === "/enquiry_profile" && task.enquiry;
      return (
        <Button
          size="small"
          variant="contained"
          startIcon={<PaymentIcon />}
          onClick={(e) => {
            stop(e);
            if (isEnquiryProfile) {
              setEnquiry && setEnquiry(task.enquiry);
              navigate("/enquiry_profile", { state: { enquiry: task.enquiry } });
            } else {
              navigate(task.link || "/accounts/payments");
            }
          }}
        >
          {isEnquiryProfile ? "Open enquiry" : "View plan"}
        </Button>
      );
    }

    if (task.type === "appointment") {
      return (
        <Button
          size="small"
          variant="outlined"
          startIcon={<OpenInNewIcon />}
          onClick={(e) => {
            stop(e);
            navigate(task.link || appointmentsListFallbackPath);
          }}
        >
          View
        </Button>
      );
    }

    if (task.type === "reschedule" || task.type === "session_approval") {
      return (
        <Button
          size="small"
          variant="contained"
          startIcon={<OpenInNewIcon />}
          onClick={(e) => {
            stop(e);
            navigate(task.link);
          }}
        >
          Review
        </Button>
      );
    }

    if (task.link) {
      return (
        <IconButton size="small" onClick={(e) => { stop(e); handleOpenTask(task); }} title="Open">
          <OpenInNewIcon fontSize="small" />
        </IconButton>
      );
    }
    return null;
  };

  return (
    <ThemeProvider theme={theme}>
      <Box className={styles.root}>
        <Paper elevation={0} className={styles.contentPaper}>
          <Box sx={{ marginBottom: 2 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box>
                <Typography variant="h4" className={styles.pageTitle}>
                  Tasks
                </Typography>
                <Typography variant="body2" className={styles.pageSubtitle} sx={{ mt: 0.5 }}>
                  Enquiries, programs, and other items assigned to you that require an action.
                </Typography>
              </Box>
            </Stack>
          </Box>

          <Box className={styles.header}>
            <GlobalSearchBar
              value={globalSearch}
              onChange={setGlobalSearch}
              placeholder="Search task, type, status…"
            />
            <Box className={styles.headerActions}>
              <Button
                startIcon={<FilterListIcon />}
                onClick={() => setFilterOpen(!filterOpen)}
                variant="outlined"
                size="small"
              >
                {filterOpen ? "Hide Filters" : "Show Filters"}
              </Button>
              <IconButton onClick={loadTasks} disabled={loading} size="small" title="Refresh">
                <RefreshIcon />
              </IconButton>
            </Box>
          </Box>

          {filterOpen ? (
            <Fade in={filterOpen}>
              <FilterPaper variant="outlined">
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center" flexWrap="wrap">
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Type</InputLabel>
                    <Select
                      value={filterType}
                      label="Type"
                      onChange={(e) => {
                        setFilterType(e.target.value);
                        setCurrentPage(1);
                      }}
                    >
                      <MenuItem value="">All types</MenuItem>
                      {Object.entries(TYPE_LABEL).map(([value, label]) => (
                        <MenuItem key={value} value={value}>{label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={filterStatus}
                      label="Status"
                      onChange={(e) => {
                        setFilterStatus(e.target.value);
                        setCurrentPage(1);
                      }}
                    >
                      <MenuItem value="">All statuses</MenuItem>
                      {statusOptions.map((s) => (
                        <MenuItem key={s} value={s}>{s}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button variant="outlined" startIcon={<RestartAltIcon />} onClick={resetFilters}>
                    Reset
                  </Button>
                </Stack>
              </FilterPaper>
            </Fade>
          ) : null}

          {loading && (
            <Box className={styles.loadingContainer}>
              <Skeleton variant="rectangular" height={60} sx={{ mb: 1 }} />
              <Skeleton variant="rectangular" height={400} />
            </Box>
          )}

          {error && !loading && (
            <Typography color="error" sx={{ py: 2 }}>
              {error}
            </Typography>
          )}

          {!loading && !error && filteredByFilters.length === 0 && (
            <Box className={styles.emptyState}>
              <InboxIcon sx={{ fontSize: 80, color: "text.secondary", mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No tasks found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {tasks.length === 0 ? "No tasks requiring action at the moment." : "Try adjusting your filters or search."}
              </Typography>
            </Box>
          )}

          {!loading && !error && displayData.length > 0 && (
            <>
              <Box className={styles.pagination}>
                <Typography variant="body2" color="text.secondary">
                  Showing {totalCount === 0 ? 0 : (page - 1) * pageSize + 1}–{Math.min(page * pageSize, totalCount)} of {totalCount} results
                </Typography>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <FormControl size="small" sx={{ minWidth: 100 }}>
                    <InputLabel>Per page</InputLabel>
                    <Select
                      value={pageSize}
                      label="Per page"
                      onChange={(e) => {
                        setPageSize(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                    >
                      {PAGE_SIZE_OPTIONS.map((n) => (
                        <MenuItem key={n} value={n}>{n}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(_, p) => setCurrentPage(p)}
                    color="primary"
                    shape="rounded"
                    showFirstButton
                    showLastButton
                  />
                </Stack>
              </Box>

              <Paper variant="outlined" className={styles.tableContainer}>
                <Table stickyHeader size="small" className={styles.enquiriesTable}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Type</TableCell>
                      <TableCell>Task</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Due</TableCell>
                      <TableCell align="right">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {displayData.map((task) => (
                      <TableRow
                        key={task.id}
                        hover
                        className={styles.tableRow}
                        sx={{ cursor: task.link ? "pointer" : "default" }}
                        onClick={() => task.link && handleOpenTask(task)}
                      >
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={0.5}>
                            {TYPE_ICON[task.type] || <TaskAltIcon fontSize="small" />}
                            <Typography variant="body2">{TYPE_LABEL[task.type] || task.type}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {task.title}
                            {task.type === "pending_payment" && task.outstanding_amount != null && (
                              <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
                                · Amount due: {typeof task.outstanding_amount === "number" ? task.outstanding_amount.toFixed(2) : task.outstanding_amount}
                              </Typography>
                            )}
                          </Typography>
                          {task.overdue && (
                            <Chip
                              size="small"
                              icon={<WarningAmberRoundedIcon />}
                              label="Overdue"
                              color="error"
                              sx={{ mt: 0.5 }}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={task.status || "—"} variant="outlined" />
                        </TableCell>
                        <TableCell>{formatDue(task.due_date)}</TableCell>
                        <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                          {renderActionButtons(task)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>

              <Box className={styles.pagination}>
                <Typography variant="body2" color="text.secondary">
                  Showing {totalCount === 0 ? 0 : (page - 1) * pageSize + 1}–{Math.min(page * pageSize, totalCount)} of {totalCount} results
                </Typography>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <FormControl size="small" sx={{ minWidth: 100 }}>
                    <InputLabel>Per page</InputLabel>
                    <Select
                      value={pageSize}
                      label="Per page"
                      onChange={(e) => {
                        setPageSize(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                    >
                      {PAGE_SIZE_OPTIONS.map((n) => (
                        <MenuItem key={n} value={n}>{n}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(_, p) => setCurrentPage(p)}
                    color="primary"
                    shape="rounded"
                    showFirstButton
                    showLastButton
                  />
                </Stack>
              </Box>
            </>
          )}
        </Paper>

        <Dialog
          open={contactedModalOpen}
          onClose={closeContactedModal}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 2 } }}
        >
          <DialogTitle sx={{ fontWeight: 600 }}>Mark as Contacted</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Add an enquiry comment and select lead temperature.
            </Typography>
            {contactedEnquiry && (
              <Typography variant="body2" sx={{ mb: 2 }}>
                {contactedEnquiry.first_name} {contactedEnquiry.last_name} (Enquiry #{contactedEnquiry.id})
              </Typography>
            )}
            <Box component="form" onSubmit={handleContactedSubmit} noValidate>
              <FormControl fullWidth size="small" sx={{ mb: 2 }} required>
                <InputLabel>Lead status</InputLabel>
                <Select
                  value={contactLeadStatus}
                  label="Lead status"
                  onChange={(e) => setContactLeadStatus(e.target.value)}
                >
                  <MenuItem value="">Select Hot / Warm / Cold</MenuItem>
                  <MenuItem value="Hot">Hot</MenuItem>
                  <MenuItem value="Warm">Warm</MenuItem>
                  <MenuItem value="Cold">Cold</MenuItem>
                </Select>
              </FormControl>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Enquiry comment"
                placeholder="Write a comment..."
                value={contactCommentText}
                onChange={(e) => setContactCommentText(e.target.value)}
                required
                size="small"
                sx={{ mb: 2 }}
              />
              {contactError && (
                <Typography color="error" variant="body2" sx={{ mb: 2 }}>
                  {contactError}
                </Typography>
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={closeContactedModal} disabled={contactSubmitLoading}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleContactedSubmit}
              disabled={contactSubmitLoading || !contactLeadStatus.trim() || !contactCommentText.trim()}
              startIcon={contactSubmitLoading ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {contactSubmitLoading ? "Saving…" : "Add comment & mark Contacted"}
            </Button>
          </DialogActions>
        </Dialog>

        <ApplicationFormModal
          open={applicationFormModalOpen}
          onClose={() => {
            setApplicationFormModalOpen(false);
            setApplicationFormEnquiry(null);
          }}
          leadId={applicationFormEnquiry?.id}
          enquiry={applicationFormEnquiry}
          onSuccess={loadTasks}
        />
      </Box>
    </ThemeProvider>
  );
}
